import React, { useState, useEffect } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { initializeApp } from "firebase/app";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faSync,
  faUsers,
  faDollarSign,
  faArrowRightFromBracket,
  faClock,
  faInfoCircle,
  faExclamationTriangle,
  faCreditCard,
  faBullseye,
} from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Table,
  Accordion,
  Badge,
  OverlayTrigger,
  Tooltip as BSTooltip,
  Form,
} from "react-bootstrap";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  Line,
  ComposedChart,
} from "recharts";

// Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyBBzepSPN2wdZfvan5GgyeeIREAV6k5BaU",
  authDomain: "loftie-4f472.firebaseapp.com",
  databaseURL: "https://loftie-4f472.firebaseio.com",
  projectId: "loftie-4f472",
  storageBucket: "loftie-4f472.appspot.com",
  messagingSenderId: "302939981606",
  appId: "1:302939981606:web:7fcb10ed6fe46fa437a3e7",
  measurementId: "G-L8VGQXX28P",
};

const app = initializeApp(firebaseConfig);
const authInstance = getAuth(app);
const db = getFirestore(app);

// Colors — unified with Loftie app palette
const C = {
  peach: "#F0D6B5",
  peachDark: "#E7AA7E",
  brown: "#635956",
  brownDark: "#3D3D3D",
  warmWhite: "#FBF7ED",
  green: "#10B981",
  coral: "#DA7833",
  red: "#CF6679",
  gray: "#9CA3AF",
  darkGray: "#4F4F4F",
  blue: "#4E6078",
  purple: "#312C6C",
  teal: "#0EA5E9",
  grid: "#E4E4E4",
  bg: "#FBF7ED",
  light: "#F6EFDF",
  white: "#FFFFFF",
  text: "#252525",
};

const STATUS_COLORS = { ACTIVE: C.green, CANCELLED: C.red, EXPIRED: C.gray, PAUSED: C.coral };
const SOURCE_COLORS = { shopify: C.blue, app_store: C.purple, play_store: C.coral, SHOPIFY: C.blue, REVENUECAT: C.purple };
const SOURCE_LABELS = { shopify: "Shopify", app_store: "App Store", play_store: "Play Store" };

// Helpers
const fmt = {
  currency: (v) => v == null ? "$0" : "$" + v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  currencyExact: (v) => v == null ? "$0.00" : "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  number: (v) => v == null ? "0" : v.toLocaleString("en-US"),
  pct: (v) => v == null ? "0%" : v.toFixed(1) + "%",
};

function staleness(computedAt) {
  if (!computedAt) return { label: "Unknown", color: C.gray, ago: "" };
  const d = new Date(computedAt);
  const hrs = (new Date() - d) / 3.6e6;
  const days = hrs / 24;
  const ago = hrs < 1 ? `${Math.round(hrs * 60)}m ago` : hrs < 24 ? `${Math.round(hrs)}h ago` : `${Math.round(days)}d ago`;
  const color = days < 1 ? C.green : days < 3 ? C.coral : C.red;
  const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  return { label, color, ago };
}

function retentionBg(pct) {
  if (pct == null) return "transparent";
  const t = pct / 100;
  return `rgb(${Math.round(255 - (255 - 16) * t)}, ${Math.round(255 - (255 - 185) * t)}, ${Math.round(255 - (255 - 129) * t)})`;
}

// Info tooltip component
function Info({ text }) {
  return (
    <OverlayTrigger placement="top" overlay={<BSTooltip>{text}</BSTooltip>}>
      <FontAwesomeIcon icon={faInfoCircle} style={{ color: C.gray, marginLeft: 6, fontSize: 12, cursor: "help" }} />
    </OverlayTrigger>
  );
}

// KPI Card
function KPICard({ icon, label, value, detail, color, info }) {
  return (
    <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", height: "100%" }}>
      <Card.Body style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <FontAwesomeIcon icon={icon} style={{ color: color || C.peach, marginRight: 8, fontSize: 13 }} />
          <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: C.gray, fontWeight: 600 }}>
            {label}
          </span>
          {info && <Info text={info} />}
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color: C.brown, lineHeight: 1.2 }}>{value}</div>
        {detail && <div style={{ fontSize: 12, color: C.darkGray, marginTop: 6, lineHeight: 1.4 }}>{detail}</div>}
      </Card.Body>
    </Card>
  );
}

// Section header
function Section({ title, info, right, children }) {
  return (
    <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 24 }}>
      <Card.Body style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h6 style={{ color: C.brown, fontWeight: 600, margin: 0 }}>
            {title}
            {info && <Info text={info} />}
          </h6>
          {right}
        </div>
        {children}
      </Card.Body>
    </Card>
  );
}

// Chart tooltip
function ChartTip({ active, payload, label, prefix }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.white, border: `1px solid ${C.grid}`, borderRadius: 8, padding: "8px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", fontSize: 13 }}>
      <div style={{ color: C.gray, marginBottom: 4, fontSize: 12 }}>{label}</div>
      {payload.map((e, i) => (
        <div key={i} style={{ color: e.color }}>{e.name}: {prefix === "$" ? fmt.currency(e.value) : fmt.number(e.value)}</div>
      ))}
    </div>
  );
}

// Pill toggle — shared style for all toggles
function PillToggle({ options, value, onChange, style }) {
  return (
    <div style={{
      display: "inline-flex", background: C.light, borderRadius: 8, padding: 2, ...style,
    }}>
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          style={{
            border: "none",
            borderRadius: 6,
            padding: "4px 12px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s ease",
            background: value === o.key ? C.peach : "transparent",
            color: value === o.key ? C.brownDark : C.gray,
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Source filter toggle
function SourceToggle({ value, onChange, style }) {
  return (
    <PillToggle
      options={[
        { key: "all", label: "Total" },
        { key: "SHOPIFY", label: "Shopify" },
        { key: "REVENUECAT", label: "RevenueCat" },
      ]}
      value={value} onChange={onChange} style={style}
    />
  );
}

// Revenue mode toggle (Monthly / Annual / Total)
function RevenueToggle({ value, onChange }) {
  return (
    <PillToggle
      options={[
        { key: "mrr", label: "Monthly" },
        { key: "annual_arr", label: "Annual" },
        { key: "total", label: "Total" },
      ]}
      value={value} onChange={onChange}
    />
  );
}

// Plan type toggle
function PlanToggle({ value, onChange, style }) {
  return (
    <PillToggle
      options={[
        { key: "all", label: "All Plans" },
        { key: "MONTHLY", label: "Monthly" },
        { key: "ANNUAL", label: "Annual" },
      ]}
      value={value} onChange={onChange} style={style}
    />
  );
}

function SubscriptionAnalytics() {
  const [user, userLoading] = useAuthState(authInstance);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [revenueMode, setRevenueMode] = useState("mrr");
  const [forecast, setForecast] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [projMetric, setProjMetric] = useState("subs");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState(null);

  const fetchAnalytics = async () => {
    try {
      const [snap, forecastSnap] = await Promise.all([
        getDoc(doc(db, "subscription-analytics", "latest")),
        getDoc(doc(db, "subscription-analytics", "forecast")),
      ]);
      if (snap.exists()) { setData(snap.data()); setError(null); }
      else setError("No analytics data found. Run the computation first.");
      if (forecastSnap.exists()) setForecast(forecastSnap.data());
    } catch (e) { setError("Failed to load: " + e.message); }
    setLoading(false);
  };

  useEffect(() => { if (user) fetchAnalytics(); }, [user]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch("https://us-central1-loftie-4f472.cloudfunctions.net/compute-subscription-analytics", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Computation failed");
      await fetchAnalytics();
    } catch (e) { setError("Refresh failed: " + e.message); }
    setRefreshing(false);
  };

  if (userLoading) return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
      <Spinner animation="border" style={{ color: C.peach }} />
    </Container>
  );
  if (!user) return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Card style={{ width: 360, borderRadius: 12, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}>
        <Card.Body style={{ padding: 32 }}>
          <h4 style={{ color: C.peach, fontWeight: 700, marginBottom: 4, textAlign: "center" }}>
            <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 8 }} />
            Subscription Analytics
          </h4>
          <p style={{ color: C.gray, fontSize: 13, textAlign: "center", marginBottom: 24 }}>Sign in to continue</p>
          {loginError && <Alert variant="danger" style={{ fontSize: 13 }}>{loginError}</Alert>}
          <Form onSubmit={async (e) => {
            e.preventDefault();
            setLoginError(null);
            try {
              await signInWithEmailAndPassword(authInstance, loginEmail, loginPassword);
            } catch (err) {
              setLoginError(err.message);
            }
          }}>
            <Form.Group className="mb-3">
              <Form.Control
                type="email" placeholder="Email" value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Control
                type="password" placeholder="Password" value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </Form.Group>
            <Button type="submit" style={{ width: "100%", borderRadius: 8, background: C.peach, border: "none", color: C.text, fontWeight: 600 }}>
              Sign In
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
  if (loading) return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
      <Spinner animation="border" style={{ color: C.peach }} />
    </Container>
  );
  if (error && !data) return <Container className="text-center mt-5"><Alert variant="danger">{error}</Alert></Container>;
  if (!data) return null;

  const { overview, mrr, churn, cohorts, timeline, predictions, cancellation_reasons, status_distribution, source_plan_matrix, payment_health, source_comparison, migration, growth } = data;
  const stale = staleness(data.computed_at);

  // Chart data prep — filter timeline by source and/or plan type
  const rawTimeline = timeline || [];
  const timelineData = rawTimeline.map((t) => {
    // Apply plan filter first
    if (planFilter !== "all") {
      const pt = t.by_type?.[planFilter] || {};
      return {
        month: t.month,
        active_end_of_month: pt.active || 0,
        new: pt.new || 0,
        cancelled: pt.cancelled || 0,
        net_change: (pt.new || 0) - (pt.cancelled || 0),
        mrr: pt.mrr || 0,
        by_source: t.by_source,
        by_type: t.by_type,
        new_mrr: t.new_mrr || 0,
      };
    }
    // Apply source filter
    if (sourceFilter !== "all") {
      const src = t.by_source?.[sourceFilter] || {};
      return {
        month: t.month,
        active_end_of_month: src.active || 0,
        new: src.new || 0,
        cancelled: src.cancelled || 0,
        net_change: (src.new || 0) - (src.cancelled || 0),
        mrr: src.mrr || 0,
        by_source: t.by_source,
        by_type: t.by_type,
        new_mrr: t.new_mrr || 0,
      };
    }
    return t;
  });

  // MRR split by plan type for the KPI card detail
  const monthlyTypeMrr = rawTimeline.length > 0 ? (rawTimeline[rawTimeline.length - 1].by_type?.MONTHLY?.mrr || 0) : 0;
  const annualTypeMrr = rawTimeline.length > 0 ? (rawTimeline[rawTimeline.length - 1].by_type?.ANNUAL?.mrr || 0) : 0;

  // Revenue display value based on toggle — monthly-only by default
  const revenueValue = revenueMode === "mrr" ? monthlyTypeMrr
    : revenueMode === "annual_arr" ? mrr?.annual_contract_arr
    : mrr?.total_mrr;
  const revenueLabel = revenueMode === "mrr" ? "Monthly MRR"
    : revenueMode === "annual_arr" ? "Annual ARR"
    : "Total MRR";
  const revenueInfo = revenueMode === "mrr"
    ? "Monthly Recurring Revenue from monthly plans only. Does not include annual contracts."
    : revenueMode === "annual_arr"
    ? "Total annual contract value from annual subscribers. This is the actual yearly revenue, not divided by 12."
    : "Combined MRR: monthly plan revenue + annual plan revenue ÷ 12.";

  const statusData = (status_distribution || []).map(s => ({ name: s.status, value: s.count, color: STATUS_COLORS[s.status] || C.gray }));
  const sourceData = Object.entries(mrr?.revenue_by_source || {}).map(([k, v]) => ({
    name: SOURCE_LABELS[k] || k, mrr: v.mrr, pct: v.pct_of_total, color: SOURCE_COLORS[k] || C.gray,
  }));
  const reasonsData = (cancellation_reasons || []).map(r => ({ name: r.reason, count: r.count, pct: r.pct }));
  const sortedCohorts = Object.entries(cohorts || {}).sort(([a], [b]) => a.localeCompare(b));
  const checkpoints = ["0", "1", "2", "3", "4", "5", "6", "9", "12", "15", "18"];

  // Stacked composition data — Shopify + RC layers for area chart
  const stackedData = rawTimeline.map((t) => {
    const shopify = t.by_source?.SHOPIFY || {};
    const rc = t.by_source?.REVENUECAT || {};
    return {
      month: t.month,
      shopify_active: shopify.active || 0,
      rc_active: rc.active || 0,
      shopify_mrr: shopify.mrr || 0,
      rc_mrr: rc.mrr || 0,
    };
  });

  // Source comparison helpers
  const sc = source_comparison || {};
  const shopifyStats = sc.SHOPIFY || {};
  const rcStats = sc.REVENUECAT || {};
  const mig = migration || {};

  // Forecast data prep
  const forecastMonths = forecast?.months || {};
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const currentForecast = forecastMonths[currentMonthKey] || {};

  // Merge forecast into timeline for chart overlay
  const timelineWithForecast = timelineData.map((t) => {
    const fc = forecastMonths[t.month];
    return {
      ...t,
      forecast_subs: fc?.total_monthly_subs || null,
      forecast_revenue: fc?.net_sales || null,
    };
  });
  // Add future forecast months not in actuals
  const lastActualMonth = timelineData.length > 0 ? timelineData[timelineData.length - 1].month : "";
  const futureForecasts = Object.entries(forecastMonths)
    .filter(([mk]) => mk > lastActualMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(0, 12)
    .map(([mk, fc]) => ({
      month: mk,
      active_end_of_month: null,
      mrr: null,
      new: null,
      cancelled: null,
      forecast_subs: fc.total_monthly_subs || null,
      forecast_revenue: fc.net_sales || null,
    }));
  const chartDataWithForecast = [...timelineWithForecast, ...futureForecasts];

  // Forecast vs Actual — build month-by-month table (past 6 + current + future 3)
  const allForecastMonths = Object.keys(forecastMonths).sort();
  const currentMonthIdx = allForecastMonths.indexOf(currentMonthKey);
  const displayMonths = allForecastMonths.filter((mk) => {
    // Show 6 months back, current, and 3 months forward
    if (mk > currentMonthKey) {
      const futureCount = allForecastMonths.filter((m) => m > currentMonthKey && m <= mk).length;
      return futureCount <= 3;
    }
    const pastCount = allForecastMonths.filter((m) => m >= mk && m < currentMonthKey).length;
    return pastCount <= 5;
  });

  // Match timeline actuals to forecast months
  const timelineByMonth = {};
  rawTimeline.forEach((t) => { timelineByMonth[t.month] = t; });

  // Build monthly forecast rows with actuals
  const monthlyForecastData = displayMonths.map((mk) => {
    const fc = forecastMonths[mk] || {};
    const tl = timelineByMonth[mk] || {};
    const isCurrent = mk === currentMonthKey;
    const isFuture = mk > currentMonthKey;
    const byType = tl.by_type || {};
    const monthly = byType.MONTHLY || {};
    const annual = byType.ANNUAL || {};
    const hasActuals = tl.active_end_of_month != null && tl.active_end_of_month > 0;
    return {
      month: mk,
      monthLabel: new Date(mk + "-01").toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      isCurrent,
      isFuture,
      // Monthly subscribers (from by_type)
      actual_total_monthly: hasActuals ? (monthly.active || 0) : null,
      forecast_total_monthly: fc.total_monthly_subs || 0,
      actual_new_monthly: hasActuals ? (monthly.new || 0) : null,
      forecast_new_monthly: fc.total_new_monthly || 0,
      actual_existing_monthly: hasActuals ? (monthly.active || 0) - (monthly.new || 0) : null,
      forecast_existing_monthly: fc.existing_monthly || 0,
      // Annual subscribers (from by_type)
      actual_total_annual: hasActuals ? (annual.active || 0) : null,
      forecast_total_annual: fc.new_annual_subs || 0,
      actual_new_annual: hasActuals ? (annual.new || 0) : null,
      forecast_new_annual: fc.new_annual_subs || 0,
      // All subscribers
      actual_total_subs: hasActuals ? tl.active_end_of_month : null,
      forecast_total_subs: (fc.total_monthly_subs || 0) + (fc.new_annual_subs || 0),
      actual_new: hasActuals ? (tl.new || 0) : null,
      // Revenue
      actual_mrr: hasActuals ? tl.mrr : null,
      forecast_revenue: fc.net_sales || 0,
      // Annual revenue
      actual_annual_mrr: hasActuals ? (annual.mrr || 0) : null,
      forecast_annual_revenue: fc.annual_recognized_revenue || 0,
    };
  });

  // Current month summary for the KPI-style row
  const currentForecastRow = monthlyForecastData.find((r) => r.isCurrent);
  const hasForecast = monthlyForecastData.length > 0;

  // Growth & projection data
  const gr = growth || {};
  const growthRates = gr.growth_rates || [];
  const projections = gr.projections || {};
  const currentScenario = projections.current || [];
  const conservativeScenario = projections.conservative || [];
  const optimisticScenario = projections.optimistic || [];

  // Build projection chart data: actual timeline + 3 scenario lines + plan
  const projectionChartData = [
    ...rawTimeline.slice(-12).map((t) => ({
      month: t.month,
      actual_subs: t.active_end_of_month,
      actual_mrr: t.mrr,
      plan_subs: forecastMonths[t.month]?.total_monthly_subs || null,
      plan_mrr: forecastMonths[t.month]?.net_sales || null,
    })),
    ...currentScenario.map((p, i) => ({
      month: p.month,
      actual_subs: null,
      actual_mrr: null,
      projected_subs: p.projected_subs,
      projected_mrr: p.projected_mrr,
      conservative_subs: conservativeScenario[i]?.projected_subs || null,
      optimistic_subs: optimisticScenario[i]?.projected_subs || null,
      conservative_mrr: conservativeScenario[i]?.projected_mrr || null,
      optimistic_mrr: optimisticScenario[i]?.projected_mrr || null,
      plan_subs: forecastMonths[p.month]?.total_monthly_subs || null,
      plan_mrr: forecastMonths[p.month]?.net_sales || null,
    })),
  ];

  // New vs Retained MRR data from timeline
  const newVsRetainedData = rawTimeline.slice(-12).map((t) => {
    const newMrr = t.new_mrr || 0;
    const totalMrr = t.mrr || 0;
    return {
      month: t.month,
      retained_mrr: Math.max(totalMrr - newMrr, 0),
      new_mrr: newMrr,
    };
  });

  // Source × Plan matrix
  const matrixSources = Object.keys(source_plan_matrix || {}).sort();
  const matrixTypes = ["MONTHLY", "ANNUAL"];

  // MoM deltas from last two timeline entries
  const prevMonth = rawTimeline.length >= 2 ? rawTimeline[rawTimeline.length - 2] : null;
  const currMonth = rawTimeline.length >= 1 ? rawTimeline[rawTimeline.length - 1] : null;
  const momSubs = prevMonth && currMonth && prevMonth.active_end_of_month > 0
    ? ((currMonth.active_end_of_month / prevMonth.active_end_of_month - 1) * 100).toFixed(1) : null;
  const momMrr = prevMonth && currMonth && prevMonth.mrr > 0
    ? ((currMonth.mrr / prevMonth.mrr - 1) * 100).toFixed(1) : null;

  // MRR waterfall data (last month vs previous)
  const lastTl = rawTimeline.length > 0 ? rawTimeline[rawTimeline.length - 1] : {};
  const prevTl = rawTimeline.length > 1 ? rawTimeline[rawTimeline.length - 2] : {};
  const wfStart = prevTl.mrr || 0;
  const wfNew = lastTl.new_mrr || 0;
  const wfChurned = lastTl.churned_mrr || 0;
  const wfEnd = lastTl.mrr || 0;
  const waterfallData = [
    { name: "Starting MRR", spacer: 0, value: wfStart, fill: C.blue },
    { name: "New", spacer: wfStart, value: wfNew, fill: C.green },
    { name: "Churned", spacer: wfEnd, value: wfChurned, fill: C.red },
    { name: "Ending MRR", spacer: 0, value: wfEnd, fill: wfEnd >= wfStart ? C.green : C.red },
  ];

  // MoM arrow helper
  const MomArrow = ({ value, inverse }) => {
    if (value == null) return null;
    const v = parseFloat(value);
    const good = inverse ? v <= 0 : v >= 0;
    return (
      <span style={{ fontSize: 11, fontWeight: 600, color: good ? C.green : C.red, marginLeft: 4 }}>
        {v >= 0 ? "▲" : "▼"} {Math.abs(v)}% MoM
      </span>
    );
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <Container style={{ maxWidth: 1200, paddingTop: 32, paddingBottom: 60 }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h2 style={{ color: C.peach, fontWeight: 700, marginBottom: 4 }}>
              <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 10 }} />
              Subscription Analytics
            </h2>
            <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: stale.color, display: "inline-block" }} />
              <span style={{ color: C.darkGray }}>Updated {stale.label} ({stale.ago})</span>
            </div>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={handleRefresh} disabled={refreshing} style={{ borderRadius: 8 }}>
            {refreshing ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSync} style={{ marginRight: 6 }} />Refresh</>}
          </Button>
        </div>

        {/* Tab Navigation + Global Filter Bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <PillToggle
            options={[
              { key: "overview", label: "Overview" },
              { key: "forecasting", label: "Forecasting" },
              { key: "sources", label: "Source Comparison" },
            ]}
            value={activeTab}
            onChange={setActiveTab}
            style={{ padding: 3 }}
          />
        </div>

        {error && <Alert variant="warning" className="mb-3" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {/* ═══════════ OVERVIEW TAB ═══════════ */}
        {activeTab === "overview" && <>

        {/* KPI Cards with MoM arrows */}
        <Row className="g-3 mb-4">
          <Col md={3}>
            <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", height: "100%" }}>
              <Card.Body style={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <FontAwesomeIcon icon={faDollarSign} style={{ color: C.blue, marginRight: 8, fontSize: 13 }} />
                  <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: C.gray, fontWeight: 600 }}>
                    {revenueLabel}
                  </span>
                  <Info text={revenueInfo} />
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.brown, lineHeight: 1.2 }}>
                  {fmt.currency(revenueValue)}
                  <MomArrow value={momMrr} />
                </div>
                <div style={{ fontSize: 12, color: C.darkGray, marginTop: 6, marginBottom: 10, lineHeight: 1.4 }}>
                  {revenueMode === "mrr"
                    ? `${fmt.number(overview?.active_monthly)} monthly subs`
                    : revenueMode === "annual_arr"
                    ? `${fmt.number(overview?.active_annual)} annual subs / ${fmt.currency(annualTypeMrr)}/mo (÷12)`
                    : <>{fmt.currency(monthlyTypeMrr)} monthly + {fmt.currency(annualTypeMrr)} annual (÷12)</>}
                </div>
                <RevenueToggle value={revenueMode} onChange={setRevenueMode} />
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", height: "100%" }}>
              <Card.Body style={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <FontAwesomeIcon icon={faUsers} style={{ color: C.green, marginRight: 8, fontSize: 13 }} />
                  <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: C.gray, fontWeight: 600 }}>Active Subscribers</span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.brown, lineHeight: 1.2 }}>
                  {fmt.number(overview?.active_subscriptions)}
                  <MomArrow value={momSubs} />
                </div>
                <div style={{ fontSize: 12, color: C.darkGray, marginTop: 6 }}>
                  {fmt.number(overview?.shopify_active)} Shopify / {fmt.number(overview?.revenuecat_active)} RC
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", height: "100%" }}>
              <Card.Body style={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <FontAwesomeIcon icon={faArrowRightFromBracket} style={{ color: C.coral, marginRight: 8, fontSize: 13 }} />
                  <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: C.gray, fontWeight: 600 }}>Monthly Churn</span>
                  <Info text="Cancellations in last 30 days / active subscribers at start of period." />
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.brown, lineHeight: 1.2 }}>{fmt.pct(churn?.monthly_churn_rate)}</div>
                <div style={{ fontSize: 12, color: C.darkGray, marginTop: 6 }}>
                  {fmt.number(churn?.monthly_churn_count)} cancelled / {(churn?.net_new_last_30d || 0) >= 0 ? "+" : ""}{fmt.number(churn?.net_new_last_30d)} net
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <KPICard
              icon={faCreditCard} color={(churn?.nrr || 100) >= 100 ? C.green : C.coral}
              label="Net Revenue Retention"
              value={fmt.pct(churn?.nrr)}
              detail={`GRR ${fmt.pct(churn?.grr)} / LTV ${fmt.currency(predictions?.estimated_ltv)}`}
              info="(Start MRR - Churned + Reactivation) / Start MRR. Measures existing customer revenue retention."
            />
          </Col>
        </Row>

        {/* Filter bar — between KPIs and charts */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <SourceToggle value={sourceFilter} onChange={setSourceFilter} />
          <PlanToggle value={planFilter} onChange={setPlanFilter} />
        </div>

        {/* Active Subscribers Over Time */}
        <Section title="Active Subscribers Over Time">
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={timelineData}>
              <defs>
                <linearGradient id="gPeach" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.peach} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={C.peach} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.gray }} />
              <YAxis tick={{ fontSize: 11, fill: C.gray }} />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="active_end_of_month" stroke={C.peach} strokeWidth={2} fill="url(#gPeach)" name="Active" />
            </AreaChart>
          </ResponsiveContainer>
        </Section>

        {/* Cohort Retention (moved up) */}
        <Section title="Cohort Retention" info="Groups subscribers by signup month. Shows % still active at each month checkpoint.">
          <div style={{ overflowX: "auto" }}>
            <Table size="sm" bordered style={{ fontSize: 12, minWidth: 700, marginBottom: 0 }}>
              <thead>
                <tr style={{ background: C.light }}>
                  <th style={{ position: "sticky", left: 0, background: C.light, zIndex: 1, minWidth: 90 }}>Cohort</th>
                  <th style={{ textAlign: "center", minWidth: 50 }}>Size</th>
                  {checkpoints.map(cp => <th key={cp} style={{ textAlign: "center", minWidth: 52 }}>Mo {cp}</th>)}
                </tr>
              </thead>
              <tbody>
                {sortedCohorts.map(([month, cd]) => (
                  <tr key={month}>
                    <td style={{ position: "sticky", left: 0, background: C.white, zIndex: 1, fontWeight: 600, color: C.brown }}>{month}</td>
                    <td style={{ textAlign: "center", color: C.darkGray }}>{fmt.number(cd.cohort_size)}</td>
                    {checkpoints.map(cp => {
                      const pct = cd.retention_pct?.[cp];
                      if (pct == null) return <td key={cp} style={{ background: "#FAFAFA", textAlign: "center", color: C.gray }}>-</td>;
                      return (
                        <td key={cp} style={{ background: retentionBg(pct), textAlign: "center", fontWeight: 500, color: pct > 60 ? "white" : C.brown }}>
                          {pct.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Section>

        {/* New vs Cancelled */}
        <Section title="New vs Cancelled Subscribers">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.gray }} />
              <YAxis tick={{ fontSize: 11, fill: C.gray }} />
              <Tooltip content={<ChartTip />} />
              <Legend />
              <Bar dataKey="new" fill={C.green} name="New" radius={[2, 2, 0, 0]} />
              <Bar dataKey="cancelled" fill={C.coral} name="Cancelled" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        {/* Accordions: Churn Details (expanded by default), MRR Movement, Cancellation Reasons, Revenue, Methodology */}
        <Accordion className="mb-4" alwaysOpen defaultActiveKey={["churn"]}>
          <Accordion.Item eventKey="churn" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header><span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Churn Details</span></Accordion.Header>
            <Accordion.Body>
              <div style={{ display: "flex", justifyContent: "center", gap: 24, fontSize: 13, color: C.darkGray, flexWrap: "wrap", marginBottom: 16 }}>
                <span>Period start: {fmt.number(churn?.active_at_period_start)}</span>
                <span>Voluntary: <span style={{ color: C.coral }}>{fmt.number(churn?.voluntary_churn)}</span></span>
                <span>Involuntary: <span style={{ color: C.red }}>{fmt.number(churn?.involuntary_churn)}</span></span>
                <span>Reactivations: <span style={{ color: C.green }}>{fmt.number(churn?.reactivations_last_30d)}</span></span>
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 48 }}>
                <div className="text-center">
                  <div style={{ fontSize: 24, fontWeight: 700, color: (churn?.nrr || 100) >= 100 ? C.green : C.coral }}>{fmt.pct(churn?.nrr)}</div>
                  <div style={{ fontSize: 11, color: C.gray }}>NRR <Info text="(Start MRR - Churned + Reactivation) / Start MRR. Existing customer retention only." /></div>
                </div>
                <div className="text-center">
                  <div style={{ fontSize: 24, fontWeight: 700, color: (churn?.grr || 100) >= 85 ? C.green : C.red }}>{fmt.pct(churn?.grr)}</div>
                  <div style={{ fontSize: 11, color: C.gray }}>GRR <Info text="(Start MRR - Churned) / Start MRR. Target: 75-85%." /></div>
                </div>
                <div className="text-center">
                  <div style={{ fontSize: 24, fontWeight: 700, color: C.coral }}>{fmt.currency(churn?.churned_mrr)}</div>
                  <div style={{ fontSize: 11, color: C.gray }}>Churned MRR</div>
                </div>
                <div className="text-center">
                  <div style={{ fontSize: 24, fontWeight: 700, color: C.green }}>{fmt.currency(churn?.new_mrr)}</div>
                  <div style={{ fontSize: 11, color: C.gray }}>New MRR</div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="waterfall" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header>
              <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>MRR Movement</span>
              <span style={{ marginLeft: 12, fontSize: 12, color: wfEnd >= wfStart ? C.green : C.red, fontWeight: 600 }}>
                {fmt.currency(wfStart)} → {fmt.currency(wfEnd)} ({wfEnd >= wfStart ? "+" : ""}{fmt.currency(wfEnd - wfStart)})
              </span>
            </Accordion.Header>
            <Accordion.Body>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={waterfallData} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.gray }} />
                  <YAxis tick={{ fontSize: 11, fill: C.gray }} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const item = waterfallData.find(d => d.name === label);
                      if (!item) return null;
                      return (
                        <div style={{ background: C.white, border: `1px solid ${C.grid}`, borderRadius: 8, padding: "8px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", fontSize: 13 }}>
                          <div style={{ color: C.gray, fontSize: 12 }}>{label}</div>
                          <div style={{ color: item.fill, fontWeight: 600 }}>{fmt.currency(item.value)}</div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="spacer" stackId="wf" fill="transparent" />
                  <Bar dataKey="value" stackId="wf" radius={[4, 4, 0, 0]}>
                    {waterfallData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 8, fontSize: 12, color: C.darkGray }}>
                <span>{fmt.currency(wfStart)} start</span>
                <span style={{ color: C.green }}>+{fmt.currency(wfNew)} new</span>
                <span style={{ color: C.red }}>-{fmt.currency(wfChurned)} churned</span>
                <span style={{ fontWeight: 600 }}>= {fmt.currency(wfEnd)} end</span>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="cancellations" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header><span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Cancellation Reasons</span></Accordion.Header>
            <Accordion.Body>
              <ResponsiveContainer width="100%" height={Math.max(200, reasonsData.length * 50)}>
                <BarChart data={reasonsData} layout="vertical" margin={{ left: 150 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.gray }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: C.brown }} width={140} />
                  <Tooltip formatter={(val, name, props) => [`${fmt.number(val)} (${props.payload.pct}%)`, "Count"]} />
                  <Bar dataKey="count" fill={C.coral} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="revenue" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header><span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Revenue Details & LTV</span></Accordion.Header>
            <Accordion.Body>
              <Table size="sm" style={{ fontSize: 13 }}>
                <tbody>
                  {[
                    ["Total MRR", fmt.currency(mrr?.total_mrr), true],
                    ["  From Monthly Plans", fmt.currency(monthlyTypeMrr)],
                    ["  From Annual Plans (÷12)", fmt.currency(annualTypeMrr)],
                    ["Annual Contract ARR", fmt.currency(mrr?.annual_contract_arr), true],
                    ["MRR per Subscriber", fmt.currencyExact(mrr?.mrr_per_subscriber)],
                    ["Free Trials (excluded)", fmt.number(mrr?.free_trial_count)],
                    ["Estimated LTV", fmt.currency(predictions?.estimated_ltv), true],
                    ["Avg Lifetime", `${predictions?.avg_lifetime_months} months`],
                  ].map(([l, v, bold], i) => (
                    <tr key={i}><td style={{ color: C.darkGray, paddingLeft: l.startsWith("  ") ? 24 : 8 }}>{l.trim()}</td><td style={{ fontWeight: bold ? 600 : 400 }}>{v}</td></tr>
                  ))}
                  {Object.entries(mrr?.revenue_by_source || {}).map(([store, info]) => (
                    <tr key={store}>
                      <td style={{ color: C.darkGray, paddingLeft: 24 }}>{SOURCE_LABELS[store] || store}</td>
                      <td>{fmt.currency(info.mrr)} ({info.pct_of_total}%)</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="methodology" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header><span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>About this data</span></Accordion.Header>
            <Accordion.Body style={{ fontSize: 13, color: C.darkGray, lineHeight: 1.7 }}>
              <strong>Sources:</strong> Shopify subscription contracts + RevenueCat in-app purchases (App Store & Play Store). RC revenue uses proceeds (after store commission).<br />
              <strong>MRR:</strong> Excludes free trial subscribers. Annual plans contribute price÷12 to MRR.<br />
              <strong>Churn:</strong> Cancellations in last 30 days / active subscribers 30 days ago. Failed payments count as involuntary churn.<br />
              <strong>NRR:</strong> (Start MRR - Churned + Reactivation) / Start MRR. Excludes new customer MRR.<br />
              <strong>Refresh:</strong> Click Refresh to recompute from current Firestore data (~10 seconds).
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        </>}

        {/* ═══════════ FORECASTING TAB ═══════════ */}
        {activeTab === "forecasting" && <>

        {/* Growth Rate KPIs */}
        <Row className="g-3 mb-4">
          <Col md={3}>
            <KPICard icon={faChartLine} color={gr.avg_sub_growth_pct >= 0 ? C.green : C.red}
              label="Sub Growth (MoM)" value={`${gr.avg_sub_growth_pct >= 0 ? "+" : ""}${gr.avg_sub_growth_pct || 0}%`}
              detail={`${fmt.number(gr.current_subs)} current subs`}
              info="Average MoM subscriber growth rate from last 6 months."
            />
          </Col>
          <Col md={3}>
            <KPICard icon={faDollarSign} color={gr.avg_mrr_growth_pct >= 0 ? C.green : C.red}
              label="MRR Growth (MoM)" value={`${gr.avg_mrr_growth_pct >= 0 ? "+" : ""}${gr.avg_mrr_growth_pct || 0}%`}
              detail={`${fmt.currency(gr.current_mrr)} current MRR`}
              info="Average MoM MRR growth rate from last 6 months."
            />
          </Col>
          <Col md={3}>
            <KPICard icon={faUsers} color={C.peachDark}
              label="Projected Subs (12mo)" value={fmt.number(currentScenario[11]?.projected_subs)}
              detail={`${fmt.number(conservativeScenario[11]?.projected_subs)} – ${fmt.number(optimisticScenario[11]?.projected_subs)} range`}
              info="12-month projection. Accounts for measured churn rate. Range = conservative (70%) to optimistic (120%) of current growth."
            />
          </Col>
          <Col md={3}>
            <KPICard icon={faDollarSign} color={C.peachDark}
              label="Projected MRR (12mo)" value={fmt.currency(currentScenario[11]?.projected_mrr)}
              detail={`${fmt.currency(conservativeScenario[11]?.projected_mrr)} – ${fmt.currency(optimisticScenario[11]?.projected_mrr)} range`}
              info="12-month MRR projection. Accounts for measured churn rate."
            />
          </Col>
        </Row>

        {/* Single Projection Chart with Subs/MRR toggle */}
        <Section title="Projection" right={
          <PillToggle options={[{ key: "subs", label: "Subscribers" }, { key: "mrr", label: "MRR" }]} value={projMetric} onChange={setProjMetric} />
        } info="Solid = actual (last 12mo), dashed = projected trend, dotted = spreadsheet plan. Shaded band = conservative-to-optimistic range.">
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={projectionChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.gray }} />
              <YAxis tick={{ fontSize: 11, fill: C.gray }} tickFormatter={projMetric === "mrr" ? (v => `$${(v / 1000).toFixed(0)}K`) : undefined} />
              <Tooltip content={<ChartTip prefix={projMetric === "mrr" ? "$" : ""} />} />
              <Legend />
              <Line type="monotone" dataKey={projMetric === "subs" ? "actual_subs" : "actual_mrr"} stroke={C.peach} strokeWidth={2.5} dot={false} name="Actual" connectNulls={false} />
              <Line type="monotone" dataKey={projMetric === "subs" ? "projected_subs" : "projected_mrr"} stroke={C.peachDark} strokeWidth={2} strokeDasharray="6 3" dot={false} name="Projected" connectNulls />
              <Line type="monotone" dataKey={projMetric === "subs" ? "plan_subs" : "plan_mrr"} stroke={C.gray} strokeWidth={1.5} strokeDasharray="2 4" dot={false} name="Plan" connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </Section>

        {/* Forecast vs Actual — always visible */}
        {hasForecast && (
          <Section title="Forecast vs Actual" info={`Targets: ${forecast?.source || "Financial Model"}. Actual: Shopify + RevenueCat from Firestore.`}>
            <div style={{ overflowX: "auto" }}>
              <Table size="sm" bordered style={{ fontSize: 12, minWidth: 600, marginBottom: 0 }}>
                <thead>
                  <tr style={{ background: C.light }}>
                    <th style={{ position: "sticky", left: 0, background: C.light, zIndex: 1, minWidth: 120 }}>Metric</th>
                    {monthlyForecastData.map((r) => (
                      <th key={r.month} style={{ textAlign: "center", minWidth: 65, fontWeight: r.isCurrent ? 700 : 600, background: r.isCurrent ? C.peach + "30" : C.light }}>
                        {r.monthLabel}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Monthly Subs", color: C.brown, getA: (r) => r.actual_total_monthly, getT: (r) => r.forecast_total_monthly, f: "num" },
                    { label: "New Monthly", color: C.green, getA: (r) => r.actual_new_monthly, getT: (r) => r.forecast_new_monthly, f: "num" },
                    { label: "Existing Monthly", color: C.blue, getA: (r) => r.actual_existing_monthly, getT: (r) => r.forecast_existing_monthly, f: "num" },
                    { label: "Annual Subs", color: C.purple, getA: (r) => r.actual_total_annual, getT: (r) => r.forecast_total_annual, f: "num" },
                    { label: "New Annual", color: C.purple, getA: (r) => r.actual_new_annual, getT: (r) => r.forecast_new_annual, f: "num" },
                    { label: "Monthly MRR", color: C.peachDark, getA: (r) => r.actual_total_monthly != null ? (r.actual_mrr || 0) - (r.actual_annual_mrr || 0) : null, getT: (r) => r.forecast_revenue, f: "$" },
                    { label: "Annual MRR (÷12)", color: C.purple, getA: (r) => r.actual_annual_mrr, getT: (r) => r.forecast_annual_revenue, f: "$" },
                  ].map((metric) => (
                    <tr key={metric.label}>
                      <td style={{ position: "sticky", left: 0, background: C.white, zIndex: 1, fontWeight: 600, color: metric.color }}>{metric.label}</td>
                      {monthlyForecastData.map((r) => {
                        const fmtFn = metric.f === "$" ? fmt.currency : fmt.number;
                        const actual = metric.getA(r);
                        const target = metric.getT(r);
                        if (actual == null) return <td key={r.month} style={{ textAlign: "center", color: C.gray, fontStyle: "italic" }}>{fmtFn(target)}</td>;
                        const delta = actual - target;
                        const vColor = delta >= 0 ? C.green : C.red;
                        return (
                          <td key={r.month} style={{ textAlign: "center", background: r.isCurrent ? C.peach + "10" : "transparent" }}>
                            <div style={{ fontWeight: 600 }}>{fmtFn(actual)}</div>
                            <div style={{ fontSize: 10, color: vColor }}>{delta >= 0 ? "+" : ""}{fmtFn(delta)}</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div style={{ fontSize: 11, color: C.gray, marginTop: 8 }}>
              <strong style={{ color: C.text }}>Bold</strong> = actual &nbsp; <em>Italic</em> = forecast &nbsp; Targets: {forecast?.source || "Financial Model"}
            </div>
          </Section>
        )}

        {/* New vs Retained MRR (below Forecast vs Actual) */}
        <Section title="New vs Retained MRR" info="Retained = Total MRR - New MRR. Shows whether growth comes from acquisition or retention.">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={newVsRetainedData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.gray }} />
              <YAxis tick={{ fontSize: 11, fill: C.gray }} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
              <Tooltip content={<ChartTip prefix="$" />} />
              <Legend />
              <Area type="monotone" dataKey="retained_mrr" stackId="1" stroke={C.blue} fill={C.blue} fillOpacity={0.4} name="Retained MRR" />
              <Area type="monotone" dataKey="new_mrr" stackId="1" stroke={C.green} fill={C.green} fillOpacity={0.5} name="New MRR" />
            </AreaChart>
          </ResponsiveContainer>
        </Section>

        {/* Collapsed: Growth History */}
        <Accordion className="mb-4" alwaysOpen>

          {growthRates.length > 0 && (
            <Accordion.Item eventKey="growth" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
              <Accordion.Header><span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>MoM Growth Rate History</span></Accordion.Header>
              <Accordion.Body>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={growthRates}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.gray }} />
                    <YAxis tick={{ fontSize: 11, fill: C.gray }} tickFormatter={v => `${v}%`} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Legend />
                    <Bar dataKey="sub_growth" fill={C.peach} name="Sub Growth %" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="mrr_growth" fill={C.blue} name="MRR Growth %" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>

        </>}

        {/* ═══════════ SOURCE COMPARISON TAB ═══════════ */}
        {activeTab === "sources" && <>

        {/* 6. Source Comparison Scorecard */}
        {source_comparison && (
          <Section title="Shopify vs RevenueCat" info="Side-by-side comparison of key metrics by subscription source.">
            <Table size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
              <thead>
                <tr style={{ background: C.light }}>
                  <th style={{ width: "40%" }}></th>
                  <th style={{ textAlign: "center", color: C.blue, fontWeight: 700 }}>Shopify</th>
                  <th style={{ textAlign: "center", color: C.purple, fontWeight: 700 }}>RevenueCat</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Active Subscribers", fmt.number(shopifyStats.active), fmt.number(rcStats.active)],
                  ["MRR", fmt.currency(shopifyStats.mrr), fmt.currency(rcStats.mrr)],
                  ["ARR", fmt.currency(shopifyStats.arr), fmt.currency(rcStats.arr)],
                  ["ARPU", fmt.currencyExact(shopifyStats.arpu), fmt.currencyExact(rcStats.arpu)],
                  ["---"],
                  ["Monthly Churn Rate", fmt.pct(shopifyStats.monthly_churn_rate), fmt.pct(rcStats.monthly_churn_rate)],
                  ["Cancellations (30d)", fmt.number(shopifyStats.cancellations_30d), fmt.number(rcStats.cancellations_30d)],
                  ["  Voluntary", fmt.number(shopifyStats.voluntary_churn), fmt.number(rcStats.voluntary_churn)],
                  ["  Involuntary", fmt.number(shopifyStats.involuntary_churn), fmt.number(rcStats.involuntary_churn)],
                  ["---"],
                  ["Annual Subs", `${fmt.number(shopifyStats.annual_active)} (${shopifyStats.pct_annual}%)`, `${fmt.number(rcStats.annual_active)} (${rcStats.pct_annual}%)`],
                  ["Monthly Subs", fmt.number(shopifyStats.monthly_active), fmt.number(rcStats.monthly_active)],
                  ["Free Trials", fmt.number(shopifyStats.free_trials), fmt.number(rcStats.free_trials)],
                ].map(([label, shopVal, rcVal], i) => {
                  if (label === "---") return <tr key={i}><td colSpan={3} style={{ borderBottom: `1px solid ${C.grid}`, padding: 2 }}></td></tr>;
                  const indent = label.startsWith("  ");
                  return (
                    <tr key={i}>
                      <td style={{ color: C.darkGray, paddingLeft: indent ? 24 : 8 }}>{label.trim()}</td>
                      <td style={{ textAlign: "center", fontWeight: 500, color: C.text }}>{shopVal}</td>
                      <td style={{ textAlign: "center", fontWeight: 500, color: C.text }}>{rcVal}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Section>
        )}

        {/* 7. Stacked Composition */}
        <Row className="g-3 mb-4">
          <Col md={6}>
            <Section title="Subscriber Composition" info="Stacked view showing how the Shopify/RevenueCat mix is shifting over time.">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={stackedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.gray }} />
                  <YAxis tick={{ fontSize: 11, fill: C.gray }} />
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                  <Area type="monotone" dataKey="shopify_active" stackId="1" stroke={C.blue} fill={C.blue} fillOpacity={0.5} name="Shopify" />
                  <Area type="monotone" dataKey="rc_active" stackId="1" stroke={C.purple} fill={C.purple} fillOpacity={0.5} name="RevenueCat" />
                </AreaChart>
              </ResponsiveContainer>
            </Section>
          </Col>
          <Col md={6}>
            <Section title="MRR Composition" info="Stacked MRR showing revenue mix between Shopify and RevenueCat over time.">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={stackedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.gray }} />
                  <YAxis tick={{ fontSize: 11, fill: C.gray }} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
                  <Tooltip content={<ChartTip prefix="$" />} />
                  <Legend />
                  <Area type="monotone" dataKey="shopify_mrr" stackId="1" stroke={C.blue} fill={C.blue} fillOpacity={0.5} name="Shopify" />
                  <Area type="monotone" dataKey="rc_mrr" stackId="1" stroke={C.purple} fill={C.purple} fillOpacity={0.5} name="RevenueCat" />
                </AreaChart>
              </ResponsiveContainer>
            </Section>
          </Col>
        </Row>

        {/* Source-specific accordions */}
        <Accordion className="mb-4" alwaysOpen>
          {migration && (
            <Accordion.Item eventKey="migration" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
              <Accordion.Header>
                <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Shopify → RevenueCat Migration</span>
                {mig.total_migrations > 0 ? (
                  <span style={{ marginLeft: 12, fontSize: 12, color: C.green, fontWeight: 600 }}>{fmt.number(mig.total_migrations)} migrated / {fmt.currency(mig.recovered_mrr)} recovered</span>
                ) : (
                  <span style={{ marginLeft: 12, fontSize: 12, color: C.gray }}>No migrations yet</span>
                )}
              </Accordion.Header>
              <Accordion.Body>
                {mig.total_migrations > 0 ? (
                  <Row className="text-center">
                    <Col md={3}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: C.green }}>{fmt.number(mig.total_migrations)}</div>
                      <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase" }}>Migrated</div>
                    </Col>
                    <Col md={3}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: C.peachDark }}>{fmt.pct(mig.conversion_rate)}</div>
                      <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase" }}>Conversion Rate</div>
                    </Col>
                    <Col md={3}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: C.green }}>{fmt.currency(mig.recovered_mrr)}</div>
                      <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase" }}>Recovered MRR</div>
                    </Col>
                    <Col md={3}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: mig.net_mrr_impact >= 0 ? C.green : C.red }}>{mig.net_mrr_impact >= 0 ? "+" : ""}{fmt.currency(mig.net_mrr_impact)}</div>
                      <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase" }}>Net MRR Impact</div>
                    </Col>
                  </Row>
                ) : (
                  <div style={{ textAlign: "center", padding: "16px 0", color: C.gray }}>
                    {fmt.number(mig.total_cancelled_shopify)} cancelled Shopify subscribers — tracking for RC reactivations
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          )}
          {payment_health && (
            <Accordion.Item eventKey="payment" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
              <Accordion.Header><span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Payment Health (Shopify)</span></Accordion.Header>
              <Accordion.Body>
                <Row>
                  {Object.entries(payment_health.payment_status_breakdown || {}).sort(([,a],[,b]) => b - a).map(([status, count]) => {
                    const pct = payment_health.total_shopify > 0 ? (count / payment_health.total_shopify * 100).toFixed(1) : 0;
                    const color = status === "SUCCEEDED" ? C.green : status === "FAILED" ? C.red : C.gray;
                    return (
                      <Col key={status} md={3} className="text-center mb-3">
                        <div style={{ fontSize: 24, fontWeight: 700, color }}>{fmt.number(count)}</div>
                        <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase" }}>{status === "None" ? "No Data" : status}</div>
                        <div style={{ fontSize: 12, color: C.darkGray }}>{pct}%</div>
                      </Col>
                    );
                  })}
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          )}
          {source_plan_matrix && matrixSources.length > 0 && (
            <Accordion.Item eventKey="matrix" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
              <Accordion.Header><span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Source × Plan Breakdown</span></Accordion.Header>
              <Accordion.Body>
                <Table size="sm" bordered style={{ fontSize: 13, marginBottom: 0 }}>
                  <thead>
                    <tr style={{ background: C.light }}>
                      <th></th>
                      {matrixTypes.map(t => <th key={t} style={{ textAlign: "center" }}>{t}</th>)}
                      <th style={{ textAlign: "center", fontWeight: 700 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrixSources.map(source => {
                      const row = source_plan_matrix[source] || {};
                      const totalCount = matrixTypes.reduce((sum, t) => sum + (row[t]?.count || 0), 0);
                      const totalMrr = matrixTypes.reduce((sum, t) => sum + (row[t]?.mrr || 0), 0);
                      return (
                        <tr key={source}>
                          <td style={{ fontWeight: 600, color: SOURCE_COLORS[source] || C.brown }}>{SOURCE_LABELS[source] || source}</td>
                          {matrixTypes.map(t => {
                            const cell = row[t];
                            return <td key={t} style={{ textAlign: "center" }}>{cell ? <><div style={{ fontWeight: 600 }}>{fmt.number(cell.count)}</div><div style={{ fontSize: 11, color: C.gray }}>{fmt.currency(cell.mrr)}/mo</div></> : "–"}</td>;
                          })}
                          <td style={{ textAlign: "center", background: C.light }}><div style={{ fontWeight: 700 }}>{fmt.number(totalCount)}</div><div style={{ fontSize: 11, color: C.gray }}>{fmt.currency(totalMrr)}/mo</div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>

        </>}

        {/* DEAD_CODE_START — will clean up */}
        {false && (
        <Accordion className="mb-4" alwaysOpen>
          {/* Migration Tracker */}
          {migration && (
            <Accordion.Item eventKey="migration" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
              <Accordion.Header>
                <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Shopify → RevenueCat Migration</span>
                {mig.total_migrations > 0 ? (
                  <span style={{ marginLeft: 12, fontSize: 12, color: C.green, fontWeight: 600 }}>
                    {fmt.number(mig.total_migrations)} migrated / {fmt.currency(mig.recovered_mrr)} recovered
                  </span>
                ) : (
                  <span style={{ marginLeft: 12, fontSize: 12, color: C.gray }}>No migrations yet</span>
                )}
              </Accordion.Header>
              <Accordion.Body>
                {mig.total_migrations > 0 ? (
                  <>
                    <Row className="text-center mb-3">
                      <Col md={3}>
                        <div style={{ fontSize: 32, fontWeight: 700, color: C.green }}>{fmt.number(mig.total_migrations)}</div>
                        <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>Migrated Users</div>
                      </Col>
                      <Col md={3}>
                        <div style={{ fontSize: 32, fontWeight: 700, color: C.peachDark }}>{fmt.pct(mig.conversion_rate)}</div>
                        <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Conversion Rate
                          <Info text={`${fmt.number(mig.total_migrations)} reactivated out of ${fmt.number(mig.total_cancelled_shopify)} total cancelled Shopify subscribers.`} />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div style={{ fontSize: 32, fontWeight: 700, color: C.green }}>{fmt.currency(mig.recovered_mrr)}</div>
                        <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>Recovered MRR</div>
                      </Col>
                      <Col md={3}>
                        <div style={{ fontSize: 32, fontWeight: 700, color: mig.net_mrr_impact >= 0 ? C.green : C.red }}>
                          {mig.net_mrr_impact >= 0 ? "+" : ""}{fmt.currency(mig.net_mrr_impact)}
                        </div>
                        <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Net MRR Impact
                          <Info text="Recovered MRR from RC minus the MRR those users were paying on Shopify. Positive = users paying more on RC." />
                        </div>
                      </Col>
                    </Row>
                    <div style={{ borderTop: `1px solid ${C.grid}`, paddingTop: 16 }}>
                      <h6 style={{ fontSize: 12, color: C.brown, fontWeight: 600, marginBottom: 12 }}>Time to Reactivation</h6>
                      <div style={{ display: "flex", justifyContent: "center", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
                        <div className="text-center">
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{mig.median_days_to_reactivate}d</div>
                          <div style={{ fontSize: 11, color: C.gray }}>Median</div>
                        </div>
                        <div className="text-center">
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{mig.avg_days_to_reactivate}d</div>
                          <div style={{ fontSize: 11, color: C.gray }}>Average</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
                        {[
                          ["Same day", mig.time_buckets?.same_day, C.green],
                          ["< 1 week", mig.time_buckets?.within_week, C.green],
                          ["< 1 month", mig.time_buckets?.within_month, C.peachDark],
                          ["< 3 months", mig.time_buckets?.within_quarter, C.coral],
                          ["3+ months", mig.time_buckets?.longer, C.red],
                        ].map(([label, count, color], i) => (
                          <div key={i} style={{
                            background: color + "15", border: `1px solid ${color}30`, borderRadius: 8,
                            padding: "8px 16px", textAlign: "center", minWidth: 90,
                          }}>
                            <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmt.number(count)}</div>
                            <div style={{ fontSize: 10, color: C.darkGray }}>{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "16px 0", color: C.gray }}>
                    <div style={{ fontSize: 14 }}>No migrations detected yet</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>{fmt.number(mig.total_cancelled_shopify)} cancelled Shopify subscribers — tracking for RC reactivations</div>
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* Payment Health */}
          {payment_health && (
            <Accordion.Item eventKey="payment" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
              <Accordion.Header>
                <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Payment Health (Shopify)</span>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  {Object.entries(payment_health.payment_status_breakdown || {}).sort(([,a],[,b]) => b - a).map(([status, count]) => {
                    const pct = payment_health.total_shopify > 0 ? (count / payment_health.total_shopify * 100).toFixed(1) : 0;
                    const color = status === "SUCCEEDED" ? C.green : status === "FAILED" ? C.red : C.gray;
                    return (
                      <Col key={status} md={3} className="text-center mb-3">
                        <div style={{ fontSize: 24, fontWeight: 700, color }}>{fmt.number(count)}</div>
                        <div style={{ fontSize: 11, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {status === "None" ? "No Data" : status === "UNKNOWN" ? "Unknown" : status}
                        </div>
                        <div style={{ fontSize: 12, color: C.darkGray }}>{pct}%</div>
                      </Col>
                    );
                  })}
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* Source × Plan Matrix */}
          {source_plan_matrix && matrixSources.length > 0 && (
            <Accordion.Item eventKey="matrix" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
              <Accordion.Header>
                <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Source × Plan Breakdown</span>
              </Accordion.Header>
              <Accordion.Body>
                <Table size="sm" bordered style={{ fontSize: 13, marginBottom: 0 }}>
                  <thead>
                    <tr style={{ background: C.light }}>
                      <th></th>
                      {matrixTypes.map(t => <th key={t} style={{ textAlign: "center" }}>{t}</th>)}
                      <th style={{ textAlign: "center", fontWeight: 700 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrixSources.map(source => {
                      const row = source_plan_matrix[source] || {};
                      const totalCount = matrixTypes.reduce((sum, t) => sum + (row[t]?.count || 0), 0);
                      const totalMrr = matrixTypes.reduce((sum, t) => sum + (row[t]?.mrr || 0), 0);
                      return (
                        <tr key={source}>
                          <td style={{ fontWeight: 600, color: SOURCE_COLORS[source] || C.brown }}>
                            {SOURCE_LABELS[source] || source}
                          </td>
                          {matrixTypes.map(t => {
                            const cell = row[t];
                            return (
                              <td key={t} style={{ textAlign: "center" }}>
                                {cell ? (
                                  <div>
                                    <div style={{ fontWeight: 600 }}>{fmt.number(cell.count)}</div>
                                    <div style={{ fontSize: 11, color: C.gray }}>{fmt.currency(cell.mrr)}/mo</div>
                                  </div>
                                ) : <span style={{ color: C.gray }}>-</span>}
                              </td>
                            );
                          })}
                          <td style={{ textAlign: "center", background: C.light }}>
                            <div style={{ fontWeight: 700 }}>{fmt.number(totalCount)}</div>
                            <div style={{ fontSize: 11, color: C.gray }}>{fmt.currency(totalMrr)}/mo</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          )}

          {/* Revenue Details + LTV */}
          <Accordion.Item eventKey="revenue" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header>
              <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Revenue Details & LTV</span>
            </Accordion.Header>
            <Accordion.Body>
              <Table size="sm" style={{ fontSize: 13 }}>
                <tbody>
                  {[
                    ["Total MRR", fmt.currency(mrr?.total_mrr), true],
                    ["ARR Estimate (MRR x 12)", fmt.currency(mrr?.arr_estimate), true],
                    ["Annual Contract ARR", fmt.currency(mrr?.annual_contract_arr), true],
                    ["MRR per Subscriber", fmt.currencyExact(mrr?.mrr_per_subscriber)],
                    ["Free Trials (excluded)", fmt.number(mrr?.free_trial_count)],
                  ].map(([l, v, bold], i) => (
                    <tr key={i}><td style={{ color: C.darkGray }}>{l}</td><td style={{ fontWeight: bold ? 600 : 400 }}>{v}</td></tr>
                  ))}
                  {Object.entries(mrr?.revenue_by_source || {}).map(([store, info]) => (
                    <tr key={store}>
                      <td style={{ color: C.darkGray, paddingLeft: 24 }}>{SOURCE_LABELS[store] || store}</td>
                      <td>{fmt.currency(info.mrr)} ({info.pct_of_total}%)</td>
                    </tr>
                  ))}
                  <tr><td colSpan={2} style={{ borderBottom: `1px solid ${C.grid}`, padding: 2 }}></td></tr>
                  <tr><td style={{ color: C.darkGray }}>Estimated LTV</td><td style={{ fontWeight: 600 }}>{fmt.currency(predictions?.estimated_ltv)}</td></tr>
                  <tr><td style={{ color: C.darkGray }}>Avg Lifetime</td><td>{predictions?.avg_lifetime_months} months</td></tr>
                </tbody>
              </Table>
            </Accordion.Body>
          </Accordion.Item>

          {/* Cancellation Reasons */}
          <Accordion.Item eventKey="cancellations" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header>
              <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>Cancellation Reasons</span>
              <span style={{ marginLeft: 12, fontSize: 12, color: C.gray }}>From in-app survey</span>
            </Accordion.Header>
            <Accordion.Body>
              <ResponsiveContainer width="100%" height={Math.max(200, reasonsData.length * 55)}>
                <BarChart data={reasonsData} layout="vertical" margin={{ left: 150 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.gray }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: C.brown }} width={140} />
                  <Tooltip formatter={(val, name, props) => [`${fmt.number(val)} (${props.payload.pct}%)`, "Count"]} />
                  <Bar dataKey="count" fill={C.coral} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Accordion.Body>
          </Accordion.Item>

          {/* About this data */}
          <Accordion.Item eventKey="methodology" style={{ borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: 8 }}>
            <Accordion.Header>
              <FontAwesomeIcon icon={faInfoCircle} style={{ color: C.blue, marginRight: 8 }} />
              <span style={{ fontWeight: 600, color: C.brown, fontSize: 14 }}>About this data</span>
            </Accordion.Header>
            <Accordion.Body style={{ fontSize: 13, color: C.darkGray, lineHeight: 1.7 }}>
              <strong>Sources:</strong> Shopify subscription contracts + RevenueCat in-app purchases (App Store & Play Store).<br />
              <strong>MRR:</strong> Excludes subscribers on free trial months and failed payments. RevenueCat prices fall back to $5.99/mo or $49.99/yr when actual price is unavailable.<br />
              <strong>Churn:</strong> Cancellations in last 30 days / active subscribers 30 days ago. Contracts with cancelledAt are always treated as cancelled. Failed payments are treated as cancelled.<br />
              <strong>Cohort Retention:</strong> Uses creation and cancellation dates to calculate actual tenure. Uses 30-day months as approximation.<br />
              <strong>LTV:</strong> Average retention curve months x average MRR per subscriber. Accuracy improves as more cohort data accumulates.<br />
              <strong>Refresh:</strong> Data is computed on demand — click Refresh to recompute from current Firestore data. Computation takes ~10 seconds.
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        )}

        {/* Back */}
        <div className="text-center" style={{ marginTop: 32 }}>
          <a href="/" style={{ color: C.brown, textDecoration: "none" }}>Back to Dashboard</a>
        </div>
      </Container>
    </div>
  );
}

export default SubscriptionAnalytics;
